import { useState } from "react";
import { api, errMsg, MONTHS_IT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, Gear, Eye, FolderOpen } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MonthClose() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [label, setLabel] = useState("");

  const urlOf = (d) => `${BACKEND}/api/archive/file/${d.id}`;

  const print = async () => {
    setBusy(true);
    setDocs([]);
    try {
      const r = await api.post("/weights/export/chiusura-mese/files", { year, month });
      const lista = r.data.documents || [];
      const falliti = r.data.failed || [];
      setDocs(lista);
      setLabel(r.data.label || "");
      let bloccate = 0;
      lista.forEach((d) => { if (!window.open(urlOf(d), "_blank")) bloccate += 1; });
      if (falliti.length) {
        falliti.forEach((n) => toast.error(`${n}: non salvato in archivio, riprova la stampa`));
      } else if (bloccate) {
        toast.warning(`${lista.length} PDF archiviati: il browser ha bloccato le schede, aprili dall'elenco`);
      } else {
        toast.success(`${lista.length} PDF generati, aperti e archiviati in ${r.data.label}`);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="month-close-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Stampa Fine Mese</h1>
        <p className="text-sm text-muted-foreground mt-1">
          PDF separati della chiusura: Riepilogo Presenze, Riepilogo Presenze Non Attivi, Riepilogo
          Generale Pesi, Totale Pesi Disossatori, Prelievo Attrezzature e DPI e una scheda
          per ogni cottimista. Si aprono
          in schede distinte e vengono archiviati nella cartella del mese, sottocartella Chiusura Mensile.
        </p>
      </div>

      <div className="rounded-sm border border-border bg-card p-6 max-w-xl space-y-4">
        <div className="flex flex-wrap gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="h-10 w-[170px] rounded-sm" data-testid="close-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-10 w-[120px] rounded-sm" data-testid="close-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[year - 2, year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button size="lg" className="rounded-sm font-semibold w-full" disabled={busy}
          data-testid="close-print" onClick={print}>
          {busy ? <Gear size={18} className="mr-2 animate-spin" /> : <Printer size={18} className="mr-2" />}
          {busy ? "Preparazione dei PDF..." : `STAMPA CHIUSURA ${MONTHS_IT[month - 1].toUpperCase()} ${year}`}
        </Button>
        <p className="text-xs text-muted-foreground">
          Consenti le finestre popup per far aprire tutte le schede: in ogni caso trovi i PDF
          nell'elenco qui sotto e in Archivio Documenti.
        </p>
      </div>

      {docs.length > 0 && (
        <div className="rounded-sm border border-border bg-card max-w-3xl" data-testid="close-docs">
          <div className="bg-secondary px-3 py-2 text-sm font-head font-bold flex items-center gap-1.5">
            <FolderOpen size={15} /> Archivio {label} › Chiusura Mensile — {docs.length} PDF
          </div>
          <ul className="divide-y divide-border">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2"
                data-testid={`close-doc-${d.id}`}>
                <span className="text-sm truncate">{d.name}</span>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="rounded-sm"
                    data-testid={`close-doc-open-${d.id}`}
                    onClick={() => window.open(urlOf(d), "_blank")}>
                    <Eye size={14} className="mr-1.5" /> APRI
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm"
                    data-testid={`close-doc-print-${d.id}`}
                    onClick={() => {
                      const w = window.open(urlOf(d), "_blank");
                      if (w) w.addEventListener("load", () => w.print());
                    }}>
                    <Printer size={14} className="mr-1.5" /> STAMPA
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
